import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AutoVideo } from "@/components/AutoVideo";

type HighlightRow = {
  id: string;
  content_type: string;
  content_id: string;
  title: string;
  description: string | null;
  project_presentation: string | null;
  image_url: string | null;
  image_credit: string | null;
  image_alt: string | null;
  display_order: number;
};

const TYPE_LABEL: Record<string, string> = {
  article: "Article",
  instagram: "Instagram",
  podcast: "Podcast",
};

function isVideoUrl(url: string): boolean {
  return /\.(webm|mp4|mov)(\?|$)/i.test(url);
}

export const highlightsQueryOptions = queryOptions({
  queryKey: ["home-highlights"],
  queryFn: async () => {
    const { data, error } = await supabase
      .from("highlights")
      .select(
        "id,content_type,content_id,title,description,project_presentation,image_url,image_credit,image_alt,display_order",
      )
      .eq("is_published", true)
      .order("display_order", { ascending: true });
    if (error) throw error;
    const highlights = (data ?? []) as HighlightRow[];
    if (highlights.length === 0) return { highlights, links: {} as Record<string, string> };

    const idsFor = (type: string) =>
      highlights.filter((h) => h.content_type === type).map((h) => h.content_id);

    const [articles, posts, episodes] = await Promise.all([
      idsFor("article").length
        ? supabase.from("articles").select("id,href").in("id", idsFor("article"))
        : Promise.resolve({ data: [], error: null }),
      idsFor("instagram").length
        ? supabase.from("instagram_posts").select("id,reel_url").in("id", idsFor("instagram"))
        : Promise.resolve({ data: [], error: null }),
      idsFor("podcast").length
        ? supabase
            .from("podcast_episodes")
            .select("id,apple_url,spotify_url")
            .in("id", idsFor("podcast"))
        : Promise.resolve({ data: [], error: null }),
    ]);

    const links: Record<string, string> = {};
    for (const a of (articles.data ?? []) as { id: string; href: string | null }[]) {
      if (a.href) links[a.id] = a.href;
    }
    for (const p of (posts.data ?? []) as { id: string; reel_url: string }[]) {
      if (p.reel_url) links[p.id] = p.reel_url;
    }
    for (const e of (episodes.data ?? []) as {
      id: string;
      apple_url: string | null;
      spotify_url: string | null;
    }[]) {
      const url = e.apple_url || e.spotify_url;
      if (url) links[e.id] = url;
    }
    return { highlights, links };
  },
});

export function HomeHighlights() {
  const { data } = useSuspenseQuery(highlightsQueryOptions);
  if (data.highlights.length === 0) return null;

  return (
    <section
      aria-labelledby="highlights-title"
      className="mt-20 border-t border-border pt-16 max-sm:-mt-16 max-sm:pt-16"
    >
      <h2
        id="highlights-title"
        className="font-display text-3xl tracking-tight text-foreground sm:text-4xl"
      >
        À la une
      </h2>
      <div className="mt-8 space-y-16">
        {data.highlights.map((h) => {
          const href = data.links[h.content_id];
          const label = TYPE_LABEL[h.content_type] ?? h.content_type;
          const title = href ? (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="no-link-underline transition-colors hover:text-ocean"
            >
              {h.title}
            </a>
          ) : (
            h.title
          );
          return (
            <article key={h.id} className="flex flex-col items-center space-y-10 text-center">
              <div className="flex flex-col items-center">
                <span className="rounded-full border border-ocean/50 px-2.5 py-0.5 text-[0.7rem] font-medium uppercase tracking-[0.12em] text-ocean">
                  {label}
                </span>
                <h3 className="mt-4 font-serif text-2xl leading-snug sm:text-3xl">{title}</h3>
                {h.project_presentation && (
                  <div className="mt-6 max-w-2xl space-y-3 text-sm leading-relaxed text-foreground/85">
                    {h.project_presentation
                      .split(/\n+/)
                      .filter(Boolean)
                      .map((p, i) => (
                        <p key={i}>{p}</p>
                      ))}
                  </div>
                )}
              </div>

              {h.image_url && (
                <figure className="m-0 w-full max-w-2xl">
                  <div className="relative mx-auto aspect-[16/10] w-full overflow-hidden rounded-2xl border border-border bg-muted/30">
                    {isVideoUrl(h.image_url) ? (
                      <AutoVideo
                        src={h.image_url}
                        className="absolute inset-0 h-full w-full object-cover"
                        ariaHidden
                      />
                    ) : (
                      <img
                        src={h.image_url}
                        alt={h.image_alt || h.title}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                    )}
                  </div>
                  {h.image_credit && (
                    <figcaption className="mt-2 mb-6 text-left text-xs uppercase tracking-[0.18em] text-muted-foreground">
                      Crédits : {h.image_credit}
                    </figcaption>
                  )}
                </figure>
              )}

              {h.description && (
                <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
                  {h.description}
                </p>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}
